import { Sparkles } from "lucide-react"
import React from "react"
import { Link } from "react-router-dom"
import { Badge, Button, SummaryOverviewCard, type SummaryOverviewCardFields } from "randmarcomps"
import type { OpportunityEntry } from "randmar-api-client"
import { useManufacturer } from "../../contexts/WithManufacturer"
import Path from "../../routes/Path"
import type { ManufacturerBid } from "../../stores/search/SearchResult"
import { FormatDateFromString } from "../util/DateTimeUtil"

interface IProps {
  opportunities: ManufacturerBid | OpportunityEntry
}

const isLegacyOpportunity = (
  opportunity: ManufacturerBid | OpportunityEntry
): opportunity is ManufacturerBid => {
  // Legacy ManufacturerBid type from MobX store has camelCase properties
  return "bidNumber" in opportunity
}

const OpportunityCard: React.FC<IProps> = ({ opportunities }) => {
  const { applicationId } = useManufacturer()

  const { active, bidNumber, opportunity, startDate, endDate, lastUpdate, manufacturerId } = React.useMemo(() => {
    if (isLegacyOpportunity(opportunities)) {
      return {
        active: opportunities.active,
        bidNumber: opportunities.bidNumber,
        opportunity: opportunities.opportunity,
        startDate: opportunities.startDate,
        endDate: opportunities.endDate,
        lastUpdate: opportunities.lastUpdate,
        manufacturerId: opportunities.manufacturerId,
      }
    }
    return {
      active: opportunities.Active,
      bidNumber: opportunities.BidNumber,
      opportunity: opportunities.Opportunity,
      startDate: opportunities.StartDate,
      endDate: opportunities.EndDate,
      lastUpdate: opportunities.LastUpdate,
      manufacturerId: opportunities.ManufacturerId,
    }
  }, [opportunities])

  const statusVariant = active ? ("success" as const) : ("secondary" as const)
  const statusText = active ? "Active" : "Inactive"
  const summary = React.useMemo(() => {
    const fallback = "Opportunity overview."
    const raw = (opportunity || "").trim()
    if (!raw) return fallback

    const firstSentenceMatch = raw.match(/^[\s\S]*?[.!?](?:\s|$)/)
    const firstSentence = (firstSentenceMatch?.[0] || raw).trim()
    const cleaned = firstSentence.trim()
    if (!cleaned) return fallback

    const limited = cleaned.length > 140 ? `${cleaned.slice(0, 137).trimEnd()}...` : cleaned
    if (/[.!?]$/.test(limited)) return limited
    return `${limited}.`
  }, [opportunity])

  const formattedStartDate = React.useMemo(() => (startDate ? FormatDateFromString(startDate) : "—"), [startDate])
  const formattedEndDate = React.useMemo(() => (endDate ? FormatDateFromString(endDate) : "—"), [endDate])
  const formattedLastUpdate = React.useMemo(
    () => (lastUpdate ? FormatDateFromString(lastUpdate) : "—"),
    [lastUpdate]
  )

  const fields = React.useMemo<SummaryOverviewCardFields>(() => {
    const opportunityValue = (opportunity || "").trim()
    return [
      {
        label: "Status",
        value: (
          <Badge variant={statusVariant} size="sm" className="w-fit">
            {statusText}
          </Badge>
        ),
      },
      { label: "Start Date", value: formattedStartDate },
      { label: "End Date", value: formattedEndDate },
      { label: "Last Updated", value: formattedLastUpdate },
      { label: "Manufacturer", value: <span className="font-mono">{manufacturerId || "—"}</span> },
      {
        label: "Opportunity",
        value: (
          <span className="line-clamp-2" title={opportunityValue || undefined}>
            {opportunityValue || "—"}
          </span>
        ),
      },
    ]
  }, [formattedEndDate, formattedLastUpdate, formattedStartDate, manufacturerId, opportunity, statusText, statusVariant])

  return (
    <SummaryOverviewCard
      icon={Sparkles}
      contentType="Opportunity"
      identifier={bidNumber || "—"}
      description={summary}
      fields={fields}
      className="group rounded-2xl border border-border shadow-sm transition-shadow hover:border-primary/30 hover:shadow-md"
      actions={[
        <Button key="view-opportunity" asChild variant="default" className="min-h-[44px]">
          <Link to={Path.opportunity(applicationId, bidNumber ?? "")}>View opportunity</Link>
        </Button>,
      ]}
    />
  )
}

export default OpportunityCard
